import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  Alert,
  Autocomplete,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  Switch,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Campaign,
  SmartToy,
  Shuffle,
  Category,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

// Available context tags (same meaning as in PromptGenerator: the promo is spoken between
// the song that has just finished and the song it leads into)
const AVAILABLE_TAGS = [
  { value: 'artist', label: 'Incoming Artist', description: 'Artist of the song this promo leads into (same as next_artist)' },
  { value: 'title', label: 'Incoming Title', description: 'Title of the song this promo leads into (same as next_title)' },
  { value: 'album', label: 'Incoming Album', description: 'Album of the incoming song' },
  { value: 'genre', label: 'Incoming Genre', description: 'Genre of the incoming song' },
  { value: 'previous_artist', label: 'Previous Artist', description: 'Artist of the song that has just finished' },
  { value: 'previous_title', label: 'Previous Title', description: 'Title of the song that has just finished' },
  { value: 'next_artist', label: 'Next Artist', description: 'Artist of the incoming song (same as artist)' },
  { value: 'next_title', label: 'Next Title', description: 'Title of the incoming song (same as title)' },
  { value: 'time_spoken', label: 'Time (spoken)', description: 'Air time in words, e.g. "twenty past three"' },
  { value: 'part_of_day', label: 'Part of Day', description: 'morning / afternoon / evening / night' },
  { value: 'day_name', label: 'Day Name', description: 'e.g. Thursday' },
  { value: 'date_spoken', label: 'Date (spoken)', description: 'e.g. "Thursday the tenth of September"' },
  { value: 'hour', label: 'Hour (number)', description: 'Air-time hour as a number (0-23)' },
  { value: 'day_of_week', label: 'Day of Week (number)', description: 'Day of week as a number (0-6, Sunday = 0)' },
  { value: 'date', label: 'Date (numeric)', description: 'Air date in numeric form' },
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const EMPTY_PROMO_FORM = {
  name: '',
  description: '',
  category_id: null,
  script: '',
  is_template: false,
  voice_id: null,
  prompt_id: null,
  max_length: 15,
  tone: 'casual',
  language_code: 'en',
  tags: [],
  requires_previous: false,
  requires_next: false,
  requires_current: false,
  priority: 0,
  availability_enabled: false,
  available_days: [],
  available_start_time: '',
  available_end_time: '',
  date_range_enabled: false,
  available_start_date: '',
  available_end_date: '',
};

function parsePromoSettings(promo) {
  let settings = promo?.settings;
  if (typeof settings === 'string') {
    try {
      settings = JSON.parse(settings);
    } catch (_) {
      settings = {};
    }
  }
  return settings && typeof settings === 'object' && !Array.isArray(settings) ? settings : {};
}

function availabilityFormFromPromo(promo) {
  const availability = parsePromoSettings(promo).availability || {};
  const days = Array.isArray(availability.days)
    ? availability.days.map((d) => Number(d)).filter((d) => d >= 0 && d <= 6)
    : [];
  const startTime = availability.start_time || '';
  const endTime = availability.end_time || '';
  const startDate = availability.start_date || '';
  const endDate = availability.end_date || '';
  const hasDayTime = days.length > 0 || !!(startTime && endTime);
  const hasDate = !!(startDate || endDate);
  const isEnabled = availability.enabled === true || (availability.enabled !== false && (hasDayTime || hasDate));
  return {
    availability_enabled: isEnabled && hasDayTime,
    available_days: days,
    available_start_time: startTime,
    available_end_time: endTime,
    date_range_enabled: isEnabled && hasDate,
    available_start_date: startDate,
    available_end_date: endDate,
  };
}

function buildAvailabilitySettings(form) {
  const days = Array.isArray(form.available_days)
    ? form.available_days.map((d) => parseInt(d, 10)).filter((d) => d >= 0 && d <= 6)
    : [];
  const startTime = String(form.available_start_time || '').trim();
  const endTime = String(form.available_end_time || '').trim();
  const startDate = String(form.available_start_date || '').trim();
  const endDate = String(form.available_end_date || '').trim();
  const hasDayTime = !!form.availability_enabled && (days.length > 0 || (startTime && endTime));
  const hasDate = !!form.date_range_enabled && (startDate || endDate);
  if (!hasDayTime && !hasDate) {
    return { enabled: false };
  }
  const availability = { enabled: true };
  if (hasDayTime) {
    if (days.length) availability.days = days;
    if (startTime && endTime) {
      availability.start_time = startTime;
      availability.end_time = endTime;
    }
  }
  if (hasDate) {
    availability.start_date = startDate || null;
    availability.end_date = endDate || null;
  }
  return availability;
}

function formatAvailabilityChip(promo) {
  const availability = parsePromoSettings(promo).availability;
  if (!availability || availability.enabled === false) return null;
  const parts = [];
  if (Array.isArray(availability.days) && availability.days.length) {
    const labels = availability.days
      .map((d) => WEEKDAY_OPTIONS.find((o) => o.value === Number(d))?.label?.slice(0, 3))
      .filter(Boolean);
    if (labels.length) parts.push(labels.join(', '));
  }
  if (availability.start_time && availability.end_time) {
    parts.push(`${availability.start_time}–${availability.end_time}`);
  }
  if (availability.start_date || availability.end_date) {
    parts.push(`${availability.start_date || '…'} to ${availability.end_date || '…'}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

export default function PromoManager({ stationId, voices, prompts }) {
  const [categories, setCategories] = useState([]);
  const [promos, setPromos] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingPromo, setEditingPromo] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Promo form state
  const [promoForm, setPromoForm] = useState({ ...EMPTY_PROMO_FORM });

  // Category form state
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#FF9800',
  });

  useEffect(() => {
    fetchCategories();
    fetchPromos();
  }, [stationId]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/promos/categories`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      setCategories(res.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchPromos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/promos`, {
        params: { station_id: stationId },
        withCredentials: true,
      });
      setPromos(res.data || []);
    } catch (err) {
      console.error('Error fetching promos:', err);
    }
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name.trim()) {
      setError('Category name is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/ai/promos/categories`,
        { ...categoryForm, station_id: stationId },
        { withCredentials: true }
      );
      setSuccess('Category created successfully');
      setCategoryDialogOpen(false);
      setCategoryForm({ name: '', description: '', color: '#FF9800' });
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromo = async () => {
    if (!promoForm.name.trim()) {
      setError('Promo name is required');
      return;
    }
    if (!promoForm.script.trim()) {
      setError('Promo script is required');
      return;
    }
    if (promoForm.availability_enabled) {
      const startTime = String(promoForm.available_start_time || '').trim();
      const endTime = String(promoForm.available_end_time || '').trim();
      const days = Array.isArray(promoForm.available_days) ? promoForm.available_days : [];
      if ((startTime && !endTime) || (!startTime && endTime)) {
        setError('Set both start and end times, or leave both blank for all day.');
        return;
      }
      if (!startTime && !endTime && days.length === 0) {
        setError('Pick at least one day or a time window, or turn the day/time gate off.');
        return;
      }
    }
    if (promoForm.date_range_enabled && !String(promoForm.available_start_date || '').trim() && !String(promoForm.available_end_date || '').trim()) {
      setError('Set a start date, an end date, or turn the date range off.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const url = editingPromo
        ? `${API_URL}/api/ai/promos/${editingPromo.id}`
        : `${API_URL}/api/ai/promos`;
      const method = editingPromo ? 'put' : 'post';

      const payload = {
        ...promoForm,
        station_id: stationId,
        settings: {
          ...parsePromoSettings(editingPromo),
          availability: buildAvailabilitySettings(promoForm),
        },
      };
      delete payload.availability_enabled;
      delete payload.available_days;
      delete payload.available_start_time;
      delete payload.available_end_time;
      delete payload.date_range_enabled;
      delete payload.available_start_date;
      delete payload.available_end_date;

      await axios[method](
        url,
        payload,
        { withCredentials: true }
      );

      setSuccess(editingPromo ? 'Promo updated successfully' : 'Promo created successfully');
      setDialogOpen(false);
      setEditingPromo(null);
      setPromoForm({ ...EMPTY_PROMO_FORM });
      fetchPromos();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save promo');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPromo = (promo) => {
    setError('');
    setEditingPromo(promo);
    setPromoForm({
      ...EMPTY_PROMO_FORM,
      name: promo.name || '',
      description: promo.description || '',
      category_id: promo.category_id || null,
      script: promo.script || '',
      is_template: promo.is_template === true,
      voice_id: promo.voice_id || null,
      prompt_id: promo.prompt_id || null,
      max_length: promo.max_length || 15,
      tone: promo.tone || 'casual',
      language_code: promo.language_code || 'en',
      tags: promo.tags || [],
      requires_previous: promo.requires_previous === true,
      requires_next: promo.requires_next === true,
      requires_current: promo.requires_current === true,
      priority: promo.priority || 0,
      ...availabilityFormFromPromo(promo),
    });
    setDialogOpen(true);
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promo?')) return;

    try {
      await axios.delete(`${API_URL}/api/ai/promos/${promoId}`, {
        withCredentials: true,
      });
      setSuccess('Promo deleted successfully');
      fetchPromos();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete promo');
    }
  };

  const insertTag = (tag) => {
    const script = promoForm.script || '';
    const tagPlaceholder = `{${tag}}`;
    setPromoForm({
      ...promoForm,
      script: script + tagPlaceholder,
    });
  };

  const filteredPromos = selectedCategory
    ? promos.filter((p) => p.category_id === selectedCategory)
    : promos;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Promos" icon={<Campaign />} iconPosition="start" />
        <Tab label="Categories" icon={<Category />} iconPosition="start" />
      </Tabs>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Categories Sidebar */}
          <Grid item xs={12} md={3}>
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                    Categories
                  </Typography>
                </Box>

                <List>
                  <ListItem
                    button
                    selected={selectedCategory === null}
                    onClick={() => setSelectedCategory(null)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': { bgcolor: '#22d3ee20' },
                    }}
                  >
                    <ListItemText primary="All Promos" />
                  </ListItem>
                  {categories.map((category) => (
                    <ListItem
                      key={category.id}
                      button
                      selected={selectedCategory === category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': { bgcolor: '#22d3ee20' },
                      }}
                    >
                      <Campaign sx={{ mr: 1, color: category.color || '#FF9800' }} />
                      <ListItemText primary={category.name} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Promos List */}
          <Grid item xs={12} md={9}>
            <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                    Promos {selectedCategory && `(${filteredPromos.length})`}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      setError('');
                      setEditingPromo(null);
                      setPromoForm({ ...EMPTY_PROMO_FORM, category_id: selectedCategory });
                      setDialogOpen(true);
                    }}
                    sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#0891b2' } }}
                  >
                    New Promo
                  </Button>
                </Box>

                {filteredPromos.length === 0 ? (
                  <Alert severity="info">No promos in this category. Create one to get started.</Alert>
                ) : (
                  <List>
                    {filteredPromos.map((promo) => {
                      const availabilityLabel = formatAvailabilityChip(promo);
                      return (
                      <React.Fragment key={promo.id}>
                        <ListItem
                          sx={{
                            bgcolor: 'var(--rs-bg)',
                            borderRadius: 1,
                            mb: 1,
                            border: '1px solid var(--rs-border)',
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {promo.name}
                                </Typography>
                                {promo.category_name && (
                                  <Chip
                                    label={promo.category_name}
                                    size="small"
                                    sx={{
                                      bgcolor: promo.category_color || '#FF9800',
                                      color: 'var(--rs-text)',
                                      fontSize: '0.7rem',
                                    }}
                                  />
                                )}
                                {promo.is_template && (
                                  <Chip label="Template" size="small" sx={{ fontSize: '0.7rem' }} />
                                )}
                                {promo.priority > 0 && (
                                  <Chip label={`Priority: ${promo.priority}`} size="small" sx={{ fontSize: '0.7rem' }} />
                                )}
                                {promo.play_count > 0 && (
                                  <Chip label={`Played: ${promo.play_count}x`} size="small" sx={{ fontSize: '0.7rem' }} />
                                )}
                                {availabilityLabel && (
                                  <Chip
                                    label={availabilityLabel}
                                    size="small"
                                    sx={{ fontSize: '0.7rem', bgcolor: '#22d3ee20', color: '#22d3ee' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography variant="body2" sx={{ color: '#999', mb: 0.5 }}>
                                  {promo.script}
                                </Typography>
                                {promo.tags && promo.tags.length > 0 && (
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                    {promo.tags.map((tag) => (
                                      <Chip
                                        key={tag}
                                        label={tag}
                                        size="small"
                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleEditPromo(promo)}
                              sx={{ color: '#22d3ee', mr: 1 }}
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleDeletePromo(promo.id)}
                              sx={{ color: '#ff6b6b' }}
                            >
                              <Delete />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: '#22d3ee' }}>
                Promo Categories
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setCategoryDialogOpen(true)}
                sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#0891b2' } }}
              >
                New Category
              </Button>
            </Box>

            {categories.length === 0 ? (
              <Alert severity="info">No categories yet. Create one to organize your promos.</Alert>
            ) : (
              <List>
                {categories.map((category) => (
                  <ListItem
                    key={category.id}
                    sx={{
                      bgcolor: 'var(--rs-bg)',
                      borderRadius: 1,
                      mb: 1,
                      border: '1px solid var(--rs-border)',
                    }}
                  >
                    <Campaign sx={{ mr: 2, color: category.color || '#FF9800' }} />
                    <ListItemText
                      primary={category.name}
                      secondary={category.description}
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={`${promos.filter((p) => p.category_id === category.id).length} promos`}
                        size="small"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Promo Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingPromo(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}
      >
        <DialogTitle sx={{ color: '#22d3ee' }}>
          {editingPromo ? 'Edit Promo' : 'Create Promo'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Promo Name"
                value={promoForm.name}
                onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={promoForm.description}
                onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Category</InputLabel>
                <Select
                  value={promoForm.category_id || ''}
                  onChange={(e) => setPromoForm({ ...promoForm, category_id: e.target.value || null })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Priority"
                value={promoForm.priority}
                onChange={(e) => setPromoForm({ ...promoForm, priority: parseInt(e.target.value) || 0 })}
                helperText="Higher priority promos selected first"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Promo Script"
                value={promoForm.script}
                onChange={(e) => setPromoForm({ ...promoForm, script: e.target.value })}
                placeholder="Enter pre-made script or template with {tags}..."
                required
              />
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1 }}>
                  Click a tag to insert it into the script
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {AVAILABLE_TAGS.map((tag) => (
                    <Chip
                      key={tag.value}
                      size="small"
                      label={`{${tag.value}}`}
                      onClick={() => insertTag(tag.value)}
                      title={tag.description}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#22d3ee30' } }}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={promoForm.is_template}
                    onChange={(e) => setPromoForm({ ...promoForm, is_template: e.target.checked })}
                    sx={{ color: '#22d3ee' }}
                  />
                }
                label="Is Template (contains {tags} that will be replaced)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Voice</InputLabel>
                <Select
                  value={promoForm.voice_id || ''}
                  onChange={(e) => setPromoForm({ ...promoForm, voice_id: e.target.value || null })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="">Default</MenuItem>
                  {voices.map((voice) => (
                    <MenuItem key={voice.id} value={voice.id}>
                      {voice.name} ({voice.voice_type === 'speechify' ? 'Speechify' : (voice.voice_type === 'elevenlabs' ? 'ElevenLabs' : 'Cloned')})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Prompt (Optional)</InputLabel>
                <Select
                  value={promoForm.prompt_id || ''}
                  onChange={(e) => setPromoForm({ ...promoForm, prompt_id: e.target.value || null })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="">None (Use script directly)</MenuItem>
                  {prompts.map((prompt) => (
                    <MenuItem key={prompt.id} value={prompt.id}>
                      {prompt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Duration (seconds)"
                value={promoForm.max_length}
                onChange={(e) => setPromoForm({ ...promoForm, max_length: parseInt(e.target.value) || 15 })}
                inputProps={{ min: 5, max: 300 }}
                helperText="Spoken length the script should be written for. 15s is a short read; 60–120s is a longer promo."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#999' }}>Tone</InputLabel>
                <Select
                  value={promoForm.tone}
                  onChange={(e) => setPromoForm({ ...promoForm, tone: e.target.value })}
                  sx={{ color: 'var(--rs-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rs-border)' } }}
                >
                  <MenuItem value="casual">Casual</MenuItem>
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="energetic">Energetic</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2, bgcolor: 'var(--rs-border)' }} />
              <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                Context Requirements
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={promoForm.requires_previous}
                    onChange={(e) => setPromoForm({ ...promoForm, requires_previous: e.target.checked })}
                    sx={{ color: '#22d3ee' }}
                  />
                }
                label="Requires Previous Track"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={promoForm.requires_next}
                    onChange={(e) => setPromoForm({ ...promoForm, requires_next: e.target.checked })}
                    sx={{ color: '#22d3ee' }}
                  />
                }
                label="Requires Next Track"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={promoForm.requires_current}
                    onChange={(e) => setPromoForm({ ...promoForm, requires_current: e.target.checked })}
                    sx={{ color: '#22d3ee' }}
                  />
                }
                label="Requires Current Track"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1, bgcolor: 'var(--rs-border)' }} />
              <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 0.5 }}>
                Availability gate
              </Typography>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
                Optional. When clocks or AI voices pick this promo at random (or from a category), it is skipped outside these days, times, and dates. Leave off to allow it any time.
              </Typography>

              <Box sx={{ p: 2, mb: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!promoForm.availability_enabled}
                      onChange={(e) => setPromoForm({ ...promoForm, availability_enabled: e.target.checked })}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' } }}
                    />
                  }
                  label="Only on certain days and times"
                />
                {promoForm.availability_enabled && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#999' }}>Available days</InputLabel>
                        <Select
                          multiple
                          value={promoForm.available_days || []}
                          label="Available days"
                          onChange={(e) => setPromoForm({
                            ...promoForm,
                            available_days: (Array.isArray(e.target.value) ? e.target.value : [])
                              .map((day) => parseInt(day, 10))
                              .filter((day) => day >= 0 && day <= 6),
                          })}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((dayValue) => {
                                const dayOption = WEEKDAY_OPTIONS.find((d) => d.value === Number(dayValue));
                                return (
                                  <Chip key={dayValue} size="small" label={dayOption?.label || dayValue} />
                                );
                              })}
                            </Box>
                          )}
                        >
                          {WEEKDAY_OPTIONS.map((dayOption) => (
                            <MenuItem key={dayOption.value} value={dayOption.value}>
                              {dayOption.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        Leave empty to allow every day during the times below.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start time"
                        type="time"
                        value={promoForm.available_start_time || ''}
                        onChange={(e) => setPromoForm({ ...promoForm, available_start_time: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End time"
                        type="time"
                        value={promoForm.available_end_time || ''}
                        onChange={(e) => setPromoForm({ ...promoForm, available_end_time: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>

              <Box sx={{ p: 2, bgcolor: 'var(--rs-bg)', borderRadius: 1, border: '1px solid var(--rs-border)' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!promoForm.date_range_enabled}
                      onChange={(e) => setPromoForm({ ...promoForm, date_range_enabled: e.target.checked })}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22d3ee' } }}
                    />
                  }
                  label="Only between these dates"
                />
                <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                  Use this for a campaign window. Leave a date blank to leave that end open.
                </Typography>
                {promoForm.date_range_enabled && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start date"
                        type="date"
                        value={promoForm.available_start_date || ''}
                        onChange={(e) => setPromoForm({ ...promoForm, available_start_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End date"
                        type="date"
                        value={promoForm.available_end_date || ''}
                        onChange={(e) => setPromoForm({ ...promoForm, available_end_date: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: promoForm.available_start_date || undefined }}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid var(--rs-border)' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#999' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreatePromo}
            disabled={loading || !promoForm.name || !promoForm.script}
            sx={{ bgcolor: '#22d3ee', color: '#0a0c11', '&:hover': { bgcolor: '#0891b2' } }}
          >
            {loading ? 'Saving...' : editingPromo ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--rs-paper)', color: 'var(--rs-text)' } }}
      >
        <DialogTitle sx={{ color: '#22d3ee' }}>Create Category</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="color"
                label="Color"
                value={categoryForm.color}
                onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid var(--rs-border)' }}>
          <Button onClick={() => setCategoryDialogOpen(false)} sx={{ color: '#999' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateCategory}
            disabled={loading || !categoryForm.name}
            sx={{ bgcolor: '#22d3ee', color: '#0a0c11', '&:hover': { bgcolor: '#0891b2' } }}
          >
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
